/**
 * Formats a duration string from various formats to a human-readable format
 * Handles ISO 8601 duration format (PT22.917114S), HH:MM:SS, and seconds
 */
export const formatDuration = (duration: string): string => {
  if (!duration || duration === '0' || duration === '00:00:00' || duration === 'PT0S') {
    return 'Pending';
  }

  // Handle ISO 8601 duration format (PT30M45S, PT2H30M, PT22.917114S, etc.)
  if (duration.startsWith('PT')) {
    try {
      // Updated regex to handle decimal seconds properly
      const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/);
      if (match) {
        const hours = parseInt(match[1] || '0');
        const minutes = parseInt(match[2] || '0');
        const totalSeconds = parseFloat(match[3] || '0');
        const seconds = Math.floor(totalSeconds);

        if (hours > 0) {
          return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        } else if (minutes > 0 || seconds > 0) {
          return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        } else {
          return '0:00';
        }
      }
    } catch (_e) {
      // Silent error handling for duration parsing
    }
  }

  // If it's already in HH:MM:SS format, return as is
  if (duration.includes(':')) {
    return duration;
  }

  // If it's in seconds, convert to MM:SS format
  const seconds = parseInt(duration);
  if (!isNaN(seconds) && seconds > 0) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  return 'N/A';
};

/**
 * Converts a duration string to total seconds for calculations
 */
export const durationToSeconds = (duration: string): number => {
  if (!duration || duration === '0' || duration === '00:00:00' || duration === 'PT0S') {
    return 0;
  }

  // Handle ISO 8601 duration format
  if (duration.startsWith('PT')) {
    try {
      const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/);
      if (match) {
        const hours = parseInt(match[1] || '0');
        const minutes = parseInt(match[2] || '0');
        const seconds = parseFloat(match[3] || '0');

        return hours * 3600 + minutes * 60 + seconds;
      }
    } catch (_e) {
      // Silent error handling for duration parsing
    }
  }

  // If it's already in HH:MM:SS format
  if (duration.includes(':')) {
    const parts = duration.split(':').map((p) => parseInt(p));
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
  }

  // If it's in seconds
  const seconds = parseFloat(duration);
  if (!isNaN(seconds)) {
    return seconds;
  }

  return 0;
};

/**
 * Formats an ISO 8601 duration (PT25M30S) to zero-padded hh:mm:ss. Used in table cells and CSV export.
 */
export const formatIsoDuration = (duration: string): string => {
  if (!duration) return 'Unknown';
  if (duration.startsWith('PT')) {
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (match) {
      const h = parseInt(match[1] || '0');
      const m = parseInt(match[2] || '0');
      const s = parseInt(match[3] || '0');
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
  }
  return duration;
};

/**
 * Formats a timestamp for display in the UI
 */
export const formatLastUpdated = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);

  if (diffSeconds < 60) {
    return `${diffSeconds}s ago`;
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else {
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
};
